import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from '@emotion/styled';
import CloseIcon from '@mui/icons-material/Close';
import { designSystem } from '@/styles/theme';
import { filterTokens } from './filterTokens';
import { useIsMobileWidth } from './useIsMobileWidth';
import { isSoftKeyboardUp } from '@/common/inputKeyboard';
import { lockBodyScroll } from '@/common/bodyScrollLock';
import { useSheetKeyboard } from '@/common/keyboard/useSheetKeyboard';

/**
 * 필터 창 — 폰(≤768px)은 아래에서 올라오는 시트, 그보다 넓으면 칩 바로 아래 작은 팝업.
 * 내용(칩 묶음)과 하단 [초기화][결과 N개 보기]는 같고 겉모양만 화면 크기에 따라 바뀐다.
 *
 * - **열려 있을 때만 그린다**(`open` 이 false 면 DOM 에 아무것도 없다). 닫힌 대화상자를 DOM 에 남겨 두면
 *   앱의 스와이프 뒤로가기가 "창이 열려 있다"고 보고 앱 전체에서 멈춘다(SwipeBack.isOverlayOpen).
 * - 시트: 바깥 누르기·손잡이/제목 줄을 아래로 끌기·뒤로가기(브라우저·안드로이드 버튼·iOS 가장자리)·Esc 로 닫힌다.
 *   뒤로가기로 닫히게 하려고 열 때 방문 기록을 한 칸 쌓고(주소는 그대로), 다른 방법으로 닫을 때는 그 칸을 먼저 되돌린 뒤
 *   `onClose`/`onApply` 를 부른다 — 그래서 `onApply` 안에서 주소(쿼리)를 바꿔도 그 칸이 아니라 원래 칸이 바뀐다.
 * - 팝업: 바깥 클릭·Esc 로 닫힌다. 목록을 밀어내지 않는다(문서 위에 뜬다).
 * - 닫으면(적용 없이) 고르던 것은 버린다 — 고르던 상태(초안)는 쓰는 쪽이 들고 있다.
 */

const { color } = filterTokens;

/** 시트가 쌓은 방문 기록 칸 표시 */
const HISTORY_MARK: string = '__filterPanel';
/** 뒤로가기(popstate)가 이 시간 안에 안 오면 그냥 닫는다 */
const HISTORY_BACK_WAIT_MS: number = 500;
/** 이만큼 아래로 끌거나 */
const DRAG_CLOSE_DISTANCE_PX: number = 90;
/** 이 속도(px/ms)보다 빠르게 튕기면 닫는다 */
const DRAG_CLOSE_VELOCITY: number = 0.5;
const POPOVER_DEFAULT_WIDTH_PX: number = 400;
/** fillBody 시트 기본 높이 */
const SHEET_FILL_HEIGHT: string = '85dvh';
/** fillBody — 내용 영역은 스크롤하지 않고 안쪽 칸이 남은 높이를 나눠 쓴다 */
const FILL_BODY_STYLE: React.CSSProperties = { overflow: 'hidden', display: 'flex', flexDirection: 'column', paddingBottom: 0 };
const POPOVER_GAP_PX: number = 8;
const VIEW_MARGIN_PX: number = 16;
/** 창 안 칸의 팝업을 창 가장자리에서 들이는 최대 폭(창 안쪽 여백 — 면접 요청 창 20px) */
const DIALOG_INSET_MAX_PX: number = 32;
/**
 * 팝업이 원하는 폭(popoverWidth)보다 좁게 열렸다는 표시 — 작은 창 안에서 창 안쪽 폭에 맞춘 경우.
 * 안의 내용(공용 달력 pickerStyles PcGrid·PickerCalendar 의 `[data-popover-narrow] &` 규칙)이 이 표시로 촘촘한 모양으로 바꾼다.
 */
const POPOVER_NARROW_ATTR: string = 'data-popover-narrow';

export interface FilterPanelControls {
    /** 창을 닫고 적용(onApply) */
    apply: () => void;
    /** 적용 없이 닫기(onClose) */
    close: () => void;
}

export interface FilterPanelProps {
    open: boolean;
    /** 팝업을 붙일 칩(PC). 시트에서는 쓰지 않는다 */
    anchorEl: HTMLElement | null;
    /** 제목 — 두 줄로 끊어야 하면 <br /> 을 넣은 노드도 된다 */
    title: React.ReactNode;
    /** 제목 오른쪽 작은 안내(예: "여러 개 고를 수 있어요") */
    subtitle?: string;
    /** 제목 줄 오른쪽 끝 버튼(예: 조건 저장하기) — 있으면 [subtitle] 대신 그린다 */
    headerAction?: React.ReactNode;
    /** 적용 없이 닫힘 */
    onClose: () => void;
    /** 결과 보기 버튼(또는 controls.apply) — 창이 닫힌 뒤 불린다 */
    onApply?: () => void;
    /** 초기화 버튼 — 없으면 버튼을 두지 않는다 */
    onReset?: () => void;
    /** 결과 보기 버튼 글자. 없으면 하단 버튼 줄이 없다(정렬처럼 누르면 바로 적용하는 창) */
    applyLabel?: string;
    applyDisabled?: boolean;
    resetLabel?: string;
    /** PC 팝업 폭 */
    popoverWidth?: number;
    /** PC 팝업 층(기본 dropdown 토큰 — MUI 창 안에서 쓰면 popover 토큰으로 올린다) */
    popoverZIndex?: number;
    /** 폰 시트 층(기본 popover 토큰). 시트 위에 또 시트를 띄울 창(쿠폰 창)은 modal 토큰으로 낮춘다 */
    sheetZIndex?: number;
    /** 폰 시트 최대 높이(기본 85dvh) */
    sheetMaxHeight?: string;
    /**
     * 폰 시트: 화면 키보드 높이를 아직 못 잰 동안(visualViewport 가린 높이 0) 대신 쓸 키보드 높이(선택).
     * 입력칸을 누르는 순간 시트를 키보드 자리로 미리 올리거나(포커스 순간 입력칸이 키보드에 가릴 자리면 iOS 가 화면 전체를 민다),
     * 새 앱이 화면 줄이기를 끈 채 받은 키보드 높이를 넘길 때 쓴다. 넘기지 않으면 예전과 같다.
     */
    keyboardPreInset?: number;
    /** 폰 시트: 키보드가 떠 있을 때 최대 높이에서 위쪽 안전 여백(상태 표시줄)도 뺀다(선택) — 키 큰 시트 제목이 상태 표시줄 밑으로 들어가지 않게 */
    keyboardSafeTop?: boolean;
    /**
     * 폰 시트: 입력칸이 있는 창(선택, 안 넘기면 예전과 같다) — UI 가이드 7.1.
     * - 키보드가 떠 있으면(새 앱처럼 화면 자체가 줄어든 경우·iOS 가 보이는 화면을 끝까지 민 경우 포함) 시트 최대 높이를 보이는 높이 안으로 줄여
     *   시트 윗부분이 화면 위로 나가지 않게 하고, 넘치는 내용은 시트 안에서 스크롤한다.
     * - 키보드가 떠 있으면 버튼 줄 아래 아이폰 안전 여백을 빼 키보드에 딱 붙인다(붙이면 버튼과 키보드 사이가 뜬다).
     * - 키보드가 다 올라온 뒤 포커스된 칸이 시트 안에서 가려져 있으면 시트 안만 스크롤해 보이게 한다(문서는 건드리지 않는다).
     */
    keyboardFit?: boolean;
    /**
     * 내용이 창을 꽉 채우고 안쪽 칸이 따로 스크롤(지역 고르기: 왼쪽 시·도 | 오른쪽 구·군, 역 검색 결과).
     * 켜면 폰 시트는 높이가 [sheetMaxHeight](기본 85dvh)로 고정, PC 팝업은 [popoverHeight] 로 고정되고 내용 영역은 스크롤하지 않는다.
     */
    fillBody?: boolean;
    /** fillBody 일 때 PC 팝업 높이(px) */
    popoverHeight?: number;
    /** 제목 줄 오른쪽 닫기(X) */
    showClose?: boolean;
    /** 하단 버튼 줄을 직접 그린다(폰 시트 안 고정 / PC 팝업 아래). 있으면 applyLabel 버튼 줄 대신 쓴다 — 누르면 바로 닫히지 않는 [저장] 등 */
    renderFooter?: (controls: FilterPanelControls) => React.ReactNode;
    children: React.ReactNode | ((controls: FilterPanelControls) => React.ReactNode);
}

// ───────── 공통 모양 ─────────

const TitleRow = styled.div`
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 12px;
`;

/** 제목 줄 닫기 — 테두리·배경 없는 X(시트·팝업 공통) */
const CloseButton = styled.button`
    flex: 0 0 auto;
    align-self: center;
    width: 36px;
    height: 36px;
    margin: -6px -8px -6px 0;
    display: flex;
    align-items: center;
    justify-content: center;
    border: none;
    background: none;
    color: ${color.textSub};
    cursor: pointer;

    svg {
        font-size: 24px;
    }
`;

const Title = styled.h2`
    margin: 0;
    font-size: 19px;
    font-weight: 800;
    color: ${color.text};
`;

const Subtitle = styled.span`
    font-size: 13px;
    color: ${color.textMuted};
    white-space: nowrap;
`;

const Footer = styled.div<{ compact: boolean }>`
    display: flex;
    gap: 10px;
    flex-shrink: 0;
    background: ${color.surface};
    padding: ${({ compact }) => (compact ? '16px 0 0' : '12px 20px calc(16px + env(safe-area-inset-bottom, 0px))')};
`;

const ResetButton = styled.button<{ compact: boolean }>`
    flex: 0 0 ${({ compact }) => (compact ? 84 : 100)}px;
    height: ${({ compact }) => (compact ? 44 : 52)}px;
    border-radius: 14px;
    border: 1px solid ${color.border};
    background: ${color.surface};
    color: ${color.textSub};
    font-family: inherit;
    font-size: ${({ compact }) => (compact ? 15 : 16)}px;
    font-weight: 800;
    cursor: pointer;
`;

const ApplyButton = styled.button<{ compact: boolean }>`
    flex: 1;
    min-width: 0;
    height: ${({ compact }) => (compact ? 44 : 52)}px;
    border-radius: 14px;
    border: none;
    background: ${color.accent};
    color: #fff;
    font-family: inherit;
    font-size: ${({ compact }) => (compact ? 15 : 16)}px;
    font-weight: 800;
    cursor: pointer;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    &:disabled {
        background: ${color.disabledBg};
        cursor: default;
    }
`;

interface PanelFooterProps {
    compact: boolean;
    applyLabel: string;
    applyDisabled: boolean;
    resetLabel: string;
    onReset?: () => void;
    onApply: () => void;
}

const PanelFooter = ({ compact, applyLabel, applyDisabled, resetLabel, onReset, onApply }: PanelFooterProps) => (
    <Footer compact={compact}>
        {onReset && (
            <ResetButton type="button" compact={compact} onClick={onReset}>
                {resetLabel}
            </ResetButton>
        )}
        <ApplyButton type="button" compact={compact} disabled={applyDisabled} onClick={onApply} aria-live="polite">
            {applyLabel}
        </ApplyButton>
    </Footer>
);

const renderContent = (children: FilterPanelProps['children'], controls: FilterPanelControls): React.ReactNode =>
    typeof children === 'function' ? children(controls) : children;

const FOCUSABLE: string = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** 창 안에서 Tab 으로 갈 수 있는 요소(보이는 것만) */
const focusablesIn = (root: HTMLElement): HTMLElement[] =>
    Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((el: HTMLElement) => el.getClientRects().length > 0);

/**
 * Tab 이 창 끝을 넘으려 하면 onEdge 를 부른다(처리했으면 true). 시트는 안에서 돌고, 팝업은 닫고 칩으로 돌아간다 —
 * MUI 창 안에서 포커스 가두기를 끄고 쓸 때(쿠폰 사용 기간 달력) 포커스가 창 뒤 페이지로 새지 않게.
 */
const handleTabEdge = (event: React.KeyboardEvent<HTMLElement>, root: HTMLElement | null, onEdge: (toFirst: boolean, items: HTMLElement[]) => void): void => {
    if (event.key !== 'Tab' || !root) return;
    const items: HTMLElement[] = focusablesIn(root);
    const active: Element | null = document.activeElement;
    const atStart: boolean = active === root || active === items[0];
    const atEnd: boolean = active === items[items.length - 1];
    if (items.length === 0 || (event.shiftKey && atStart) || (!event.shiftKey && atEnd)) {
        event.preventDefault();
        onEdge(!event.shiftKey, items);
    }
};

/** 이 창을 연 요소(칩)로 초점을 돌려준다 — 키보드·스크린리더 사용자가 제자리로 돌아오게 */
const useRestoreFocus = (panelRef: React.RefObject<HTMLElement>): void => {
    useEffect(() => {
        const previous: Element | null = document.activeElement;
        // 팝업은 처음 그릴 때 자리를 재기 전이라 숨겨져(visibility: hidden) 있어 바로 focus() 하면 안 먹는다 — 다음 프레임에
        const frame: number = window.requestAnimationFrame(() => panelRef.current?.focus({ preventScroll: true }));
        return () => {
            window.cancelAnimationFrame(frame);
            if (previous instanceof HTMLElement) previous.focus({ preventScroll: true });
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
};

// ───────── 폰: 아래 시트 ─────────

const Backdrop = styled.div`
    position: fixed;
    inset: 0;
    /* 폰 시트 — 모달(쿠폰 창 등) 위에도 뜬다 */
    z-index: ${designSystem.zIndex.popover};
    display: flex;
    align-items: flex-end;
    justify-content: center;
    background: ${color.dim};
    animation: filterPanelFade 160ms ease-out;

    @keyframes filterPanelFade {
        from { opacity: 0; }
        to { opacity: 1; }
    }
`;

const Sheet = styled.div`
    width: 100%;
    max-width: 640px;
    max-height: 85vh;
    max-height: 85dvh;
    display: flex;
    flex-direction: column;
    background: ${color.surface};
    border-radius: ${filterTokens.sheetRadius}px ${filterTokens.sheetRadius}px 0 0;
    outline: none;
    animation: filterPanelUp 220ms ease-out;

    @keyframes filterPanelUp {
        from { transform: translateY(100%); }
        to { transform: translateY(0); }
    }
`;

const DragZone = styled.div`
    flex-shrink: 0;
    padding: 10px 20px 6px;
    touch-action: none;
    cursor: grab;
    user-select: none;
`;

const Grab = styled.div`
    width: 40px;
    height: 4px;
    margin: 0 auto 14px;
    border-radius: 2px;
    background: ${color.border};
`;

const SheetContent = styled.div`
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    overscroll-behavior: contain;
    -webkit-overflow-scrolling: touch;
    padding: 0 20px 12px;
`;

/** 이 창이 지금 맨 위 창인지(시트 위 시트 — Esc 는 맨 위만 닫는다) */
const isTopmost = (el: HTMLElement | null): boolean => {
    const dialogs: HTMLElement[] = Array.from(document.querySelectorAll<HTMLElement>('[role="dialog"]'));
    return !!el && dialogs[dialogs.length - 1] === el;
};

/** 화면 키보드가 가린 높이(visualViewport) — 시트를 키보드 위로 올린다 */
const useKeyboardInset = (): { inset: number; viewHeight: number; up: boolean } => {
    const read = (): { inset: number; viewHeight: number; up: boolean } => {
        const vv: VisualViewport | undefined = window.visualViewport ?? undefined;
        // up: 가린 높이(inset)와 별개로 키보드가 떠 있는지 — 새 앱은 화면 자체가 줄어 inset 이 0, iOS 가 보이는 화면을 끝까지 밀면 inset 이 0 이다
        if (!vv) return { inset: 0, viewHeight: window.innerHeight, up: isSoftKeyboardUp() };
        return { inset: Math.max(0, Math.round(window.innerHeight - vv.height - vv.offsetTop)), viewHeight: vv.height, up: isSoftKeyboardUp() };
    };
    const [state, setState] = useState(read);
    useEffect(() => {
        const vv: VisualViewport | undefined = window.visualViewport ?? undefined;
        const onResize = (): void => setState((old) => {
            const next = read();
            return old.inset === next.inset && old.viewHeight === next.viewHeight && old.up === next.up ? old : next;
        });
        vv?.addEventListener('resize', onResize);
        vv?.addEventListener('scroll', onResize);
        window.addEventListener('resize', onResize);
        return () => {
            vv?.removeEventListener('resize', onResize);
            vv?.removeEventListener('scroll', onResize);
            window.removeEventListener('resize', onResize);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return state;
};

/** 시트 안 스크롤 칸에서 칸이 가려져 있으면 그 칸만 스크롤해 보이게 한다(문서·보이는 화면은 건드리지 않는다) */
const revealInSheet = (content: HTMLElement, field: HTMLElement): void => {
    const box: DOMRect = content.getBoundingClientRect();
    const rect: DOMRect = field.getBoundingClientRect();
    const gap: number = 12;
    if (rect.top < box.top + gap) content.scrollTop -= box.top + gap - rect.top;
    else if (rect.bottom > box.bottom - gap) content.scrollTop += Math.min(rect.bottom - (box.bottom - gap), rect.top - (box.top + gap));
};

const hasHistoryMark = (id: string): boolean =>
    (window.history.state as Record<string, unknown> | null)?.[HISTORY_MARK] === id;

/**
 * 칸을 쌓기 전 원래 칸의 스크롤 복원 방식(시트 id 별). 몸통 잠금(useBodyScrollLock, body fixed — 그 순간 scrollY 0)이 칸 쌓기보다 먼저 돌아
 * 원래 칸의 스크롤 위치가 0 으로 저장되고, 닫으며 칸을 되돌리면 브라우저(scrollRestoration=auto)가 그 0 을 비동기로 복원한다 — 잠금 해제의
 * scrollTo(원래 위치)보다 늦게 오면 내린 채 연 시트를 닫은 뒤 0.6~0.8초 뒤 맨 위로 튀었다(2026-10-07 마이페이지 탈퇴 시트).
 * 쌓는 동안 원래 칸을 'manual' 로 두고(쌓은 칸도 이어받는다) 원래 칸으로 돌아오면 되돌린다 — common/useBackToClose 와 같은 방식.
 */
const baseRestoration: Map<string, ScrollRestoration> = new Map();

const restoreBaseRestoration = (id: string): void => {
    const mode: ScrollRestoration | undefined = baseRestoration.get(id);
    if (mode === undefined || hasHistoryMark(id)) return;
    baseRestoration.delete(id);
    window.history.scrollRestoration = mode;
};

/** 시트가 열린 동안 뒤 화면이 스크롤되지 않게(공용 잠금 — 머리줄·화면 위치는 그대로, common/bodyScrollLock).
 *  시트 위 시트(쿠폰 시트 위 달력): 이미 잠겨 있으면 밑의 시트가 풀 때까지 그대로 둔다 */
const useBodyScrollLock = (): void => {
    useEffect(() => lockBodyScroll(), []);
};

const SheetPanel = ({
    title, subtitle, headerAction, onClose, onApply, onReset, applyLabel, applyDisabled = false, resetLabel = '초기화', sheetZIndex, sheetMaxHeight, showClose = false, renderFooter, fillBody = false, keyboardPreInset, keyboardSafeTop = false, keyboardFit = false, children,
}: FilterPanelProps) => {
    const idRef = useRef<string>(`${Date.now()}-${Math.random().toString(36).slice(2)}`);
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;
    /** 우리가 부른 history.back() 의 popstate 를 기다리는 중이면 그 뒤에 할 일 */
    const pendingRef = useRef<(() => void) | null>(null);
    const sheetRef = useRef<HTMLDivElement>(null);
    const [dragY, setDragY] = useState<number>(0);
    const [dragging, setDragging] = useState<boolean>(false);
    const dragRef = useRef<{ startY: number; lastY: number; lastTime: number; velocity: number } | null>(null);

    useBodyScrollLock();
    useRestoreFocus(sheetRef);
    const measuredKeyboard = useKeyboardInset();
    // keyboardFit(입력칸 있는 공용 시트 — 탈퇴 비밀번호·쿠폰 코드): 대화방 시트와 같은 화면 키보드 처리(common/keyboard/useSheetKeyboard)를 붙인다.
    // 새 앱은 keyboardWillShow 높이로 키보드와 같이 올라가고, 아이폰은 칸을 누르는 순간 키보드 자리로 먼저 올린다 — 예전엔 화면 줄이기를 기다려
    // 키보드가 다 올라온 뒤 0.5~0.6초 동안 시트가 키보드 뒤에 있다가 튀어 올랐다(2026-10-07 iOS 새 앱 쿠폰 코드·탈퇴 비밀번호 녹화).
    // 넘겨받은 keyboardPreInset(대화방 시트가 자기 훅으로 잰 값)이 있으면 그것을 쓴다.
    const ownKeyboard: boolean = keyboardFit && keyboardPreInset === undefined;
    const sheetKeyboard = useSheetKeyboard(ownKeyboard);
    const preInset: number | undefined = keyboardPreInset ?? (ownKeyboard ? sheetKeyboard.preInset : undefined);
    // 잰 키보드 높이가 없을 때만 넘겨받은(또는 미리 올린) 높이를 쓴다(안 넘기면 잰 값 그대로)
    const keyboard: { inset: number; viewHeight: number } = measuredKeyboard.inset > 0 || !preInset
        ? measuredKeyboard
        : { inset: preInset, viewHeight: window.innerHeight - preInset };
    /** keyboardFit: 가린 높이가 0 이어도 키보드가 떠 있으면 보이는 높이 안으로 맞춘다 */
    const fitKeyboard: boolean = keyboardFit && (keyboard.inset > 0 || measuredKeyboard.up);
    /**
     * 버튼 줄 아래 안전 여백을 걷을지 — 키보드를 다루는 시트(keyboardFit 또는 keyboardSafeTop: 대화방 시트)는 키보드가 떠 있으면 키보드에 붙인다(UI 가이드 7.1).
     * 대화방 시트(ThreadSheet)는 자기 훅을 써서 keyboardFit 을 넘기지 않아 여백(16 + 34)이 남았고, 지금 깔린 앱(화면 줄이기)에서
     * [보내기] 아래 ~ 키보드 도구 줄 사이가 61pt 비었다(2026-10-25 지원자 관리 합격 시트). keyboardSafeTop 은 대화방 시트·SheetDialog(이미 keyboardFit)만 쓴다.
     */
    const footerOnKeyboard: boolean = (keyboardFit || keyboardSafeTop) && (keyboard.inset > 0 || measuredKeyboard.up);
    const contentRef = useRef<HTMLDivElement>(null);
    // keyboardFit: 키보드가 다 올라와 시트가 줄어든 뒤 포커스된 칸이 시트 안에서 가려져 있으면 시트 안만 스크롤
    useEffect(() => {
        if (!fitKeyboard) return undefined;
        const timer: number = window.setTimeout(() => {
            const content: HTMLDivElement | null = contentRef.current;
            const focused: Element | null = document.activeElement;
            if (content && focused instanceof HTMLElement && content.contains(focused)) revealInSheet(content, focused);
        }, 120);
        return () => window.clearTimeout(timer);
    }, [fitKeyboard, measuredKeyboard.viewHeight, measuredKeyboard.inset]);

    // 열 때 방문 기록 한 칸(같은 주소) — 뒤로가기는 이 칸을 되돌리며 시트만 닫는다
    useEffect(() => {
        const id: string = idRef.current;
        const base: Record<string, unknown> = (window.history.state as Record<string, unknown> | null) ?? {};
        baseRestoration.set(id, window.history.scrollRestoration);
        window.history.scrollRestoration = 'manual';
        window.history.pushState({ ...base, [HISTORY_MARK]: id }, '');
        const onPopState = (): void => {
            if (hasHistoryMark(id)) return;
            restoreBaseRestoration(id);
            const pending: (() => void) | null = pendingRef.current;
            if (pending) {
                pending();
                return;
            }
            onCloseRef.current();
        };
        window.addEventListener('popstate', onPopState);
        return () => {
            window.removeEventListener('popstate', onPopState);
            // 닫는 길을 거치지 않고 사라질 때(쓰는 쪽이 open 을 바로 내림·화면 폭이 바뀌어 팝업으로 바뀜 등) 쌓은 칸을 치운다 —
            // 원래 칸으로 돌아온 뒤 스크롤 복원 방식을 되돌린다
            if (!hasHistoryMark(id)) {
                restoreBaseRestoration(id);
                return;
            }
            const onBack = (): void => {
                if (hasHistoryMark(id)) return;
                window.removeEventListener('popstate', onBack);
                restoreBaseRestoration(id);
            };
            window.addEventListener('popstate', onBack);
            window.setTimeout(() => window.removeEventListener('popstate', onBack), HISTORY_BACK_WAIT_MS);
            window.history.back();
        };
    }, []);

    /** 쌓은 칸을 먼저 되돌리고 나서 after 를 부른다 */
    const leave = useCallback((after: () => void): void => {
        if (pendingRef.current) return;
        const id: string = idRef.current;
        if (!hasHistoryMark(id)) {
            after();
            return;
        }
        let done: boolean = false;
        const finish = (): void => {
            if (done) return;
            done = true;
            pendingRef.current = null;
            if (hasHistoryMark(id)) {
                // popstate 가 안 왔다 — 두 번 되돌리지 않게 표시만 지운다
                const state: Record<string, unknown> = { ...((window.history.state as Record<string, unknown> | null) ?? {}) };
                delete state[HISTORY_MARK];
                window.history.replaceState(state, '');
            }
            restoreBaseRestoration(id);
            after();
        };
        pendingRef.current = finish;
        window.history.back();
        window.setTimeout(finish, HISTORY_BACK_WAIT_MS);
    }, []);

    const close = useCallback((): void => leave(() => onCloseRef.current()), [leave]);
    const onApplyRef = useRef(onApply);
    onApplyRef.current = onApply;
    const apply = useCallback((): void => leave(() => (onApplyRef.current ?? onCloseRef.current)()), [leave]);

    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent): void => {
            // 시트 위 시트면 맨 위만 닫는다
            if (event.key !== 'Escape' || !isTopmost(sheetRef.current)) return;
            // 밑에 깔린 MUI 창(쿠폰 창 등)까지 닫히지 않게 먼저 잡아 멈춘다
            event.stopPropagation();
            close();
        };
        window.addEventListener('keydown', onKeyDown, true);
        return () => window.removeEventListener('keydown', onKeyDown, true);
    }, [close]);

    const onPointerDown = (event: React.PointerEvent<HTMLDivElement>): void => {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        // 제목 줄의 닫기(X) 같은 버튼을 누른 것은 끌기가 아니다
        if ((event.target as HTMLElement).closest('button')) return;
        event.currentTarget.setPointerCapture?.(event.pointerId);
        dragRef.current = { startY: event.clientY, lastY: event.clientY, lastTime: performance.now(), velocity: 0 };
        setDragging(true);
    };
    const onPointerMove = (event: React.PointerEvent<HTMLDivElement>): void => {
        const drag = dragRef.current;
        if (!drag) return;
        const now: number = performance.now();
        drag.velocity = (event.clientY - drag.lastY) / Math.max(1, now - drag.lastTime);
        drag.lastY = event.clientY;
        drag.lastTime = now;
        setDragY(Math.max(0, event.clientY - drag.startY));
    };
    const onPointerEnd = (): void => {
        const drag = dragRef.current;
        dragRef.current = null;
        setDragging(false);
        if (!drag) return;
        const distance: number = drag.lastY - drag.startY;
        if (distance > DRAG_CLOSE_DISTANCE_PX || (distance > 20 && drag.velocity > DRAG_CLOSE_VELOCITY)) {
            close();
        } else {
            setDragY(0);
        }
    };

    const titleId: string = `filter-panel-title-${idRef.current}`;
    /** keyboardFit 시트의 키보드 자리(시트 아래 ~ 화면 끝)를 흰 바탕으로 — 키보드가 올라오는 사이로 뒤 화면이 비치지 않게(대화방 시트와 같은 모양) */
    const keyboardFill: number = ownKeyboard ? sheetKeyboard.inset : 0;
    return createPortal(
        <>
        {keyboardFill > 0 && (
            <div
                aria-hidden="true"
                style={{ position: 'fixed', left: 0, right: 0, bottom: 0, height: keyboardFill, background: color.surface, zIndex: sheetZIndex ?? designSystem.zIndex.popover }}
            />
        )}
        <Backdrop
            onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                if (event.target === event.currentTarget) close();
            }}
            // 키보드가 올라오면 그 위로(가린 만큼 바닥을 올린다)
            style={{ ...(sheetZIndex ? { zIndex: sheetZIndex } : {}), ...(keyboard.inset > 0 ? { bottom: keyboard.inset } : {}) }}
        >
            <Sheet
                ref={sheetRef}
                onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) =>
                    handleTabEdge(event, sheetRef.current, (toFirst: boolean, items: HTMLElement[]) => (toFirst ? items[0] : items[items.length - 1])?.focus())
                }
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                tabIndex={-1}
                style={{
                    transform: dragY > 0 ? `translateY(${dragY}px)` : undefined,
                    transition: dragging ? 'none' : 'transform 180ms ease-out',
                    ...(sheetMaxHeight ? { maxHeight: sheetMaxHeight } : {}),
                    // 꽉 채우는 창은 높이를 고정해 안쪽 칸만 스크롤한다
                    ...(fillBody ? { height: sheetMaxHeight ?? SHEET_FILL_HEIGHT } : {}),
                    ...((keyboard.inset > 0 || fitKeyboard) && !keyboardSafeTop ? { maxHeight: keyboard.viewHeight - 12, ...(fillBody ? { height: keyboard.viewHeight - 12 } : {}) } : {}),
                    ...((keyboard.inset > 0 || fitKeyboard) && keyboardSafeTop ? (() => {
                        const limit: string = `calc(${keyboard.viewHeight - 12}px - env(safe-area-inset-top, 0px))`;
                        return { maxHeight: limit, ...(fillBody ? { height: limit } : {}) };
                    })() : {}),
                }}
            >
                <DragZone onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerEnd} onPointerCancel={onPointerEnd}>
                    <Grab aria-hidden="true" />
                    <TitleRow>
                        <Title id={titleId}>{title}</Title>
                        {headerAction ?? (subtitle && <Subtitle>{subtitle}</Subtitle>)}
                        {showClose && (
                            <CloseButton type="button" aria-label="닫기" onClick={close}>
                                <CloseIcon aria-hidden />
                            </CloseButton>
                        )}
                    </TitleRow>
                </DragZone>
                <SheetContent
                    ref={contentRef}
                    style={fillBody ? FILL_BODY_STYLE : undefined}
                    // 입력 칸에 포커스가 가면(키보드가 올라오면) 그 칸이 보이게
                    onFocus={(event: React.FocusEvent<HTMLDivElement>) => {
                        const target = event.target as HTMLElement;
                        if (!target.matches('input, textarea, select')) return;
                        window.setTimeout(() => target.scrollIntoView({ block: 'nearest' }), 250);
                    }}
                >
                    {ownKeyboard ? (
                        // 칸을 누르는 순간 처리(아이폰: 키보드 자리로 먼저 올린 뒤 포커스) — useSheetKeyboard 는 이 칸에서 가장 가까운 role=dialog(이 시트)를 본다
                        <div {...sheetKeyboard.containerProps}>{renderContent(children, { apply, close })}</div>
                    ) : renderContent(children, { apply, close })}
                </SheetContent>
                {renderFooter ? (
                    // 키보드가 떠 있으면 아래 안전 여백 없이 키보드에 붙인다(UI 가이드 7.1)
                    <Footer compact={false} style={footerOnKeyboard ? { paddingBottom: 16 } : undefined}>{renderFooter({ apply, close })}</Footer>
                ) : applyLabel && (
                    <PanelFooter compact={false} applyLabel={applyLabel} applyDisabled={applyDisabled} resetLabel={resetLabel} onReset={onReset} onApply={apply} />
                )}
            </Sheet>
        </Backdrop>
        </>,
        document.body,
    );
};

// ───────── PC: 칩 아래 팝업 ─────────

const Popover = styled.div`
    position: absolute;
    /* PC 팝업 — 페이지 안 드롭다운 층(머리글 sticky 아래). MUI 창 안에서는 popoverZIndex 로 올린다 */
    z-index: ${designSystem.zIndex.dropdown};
    max-height: min(70vh, 640px);
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
    padding: 16px 18px;
    background: ${color.surface};
    border: 1px solid ${color.border};
    border-radius: 16px;
    box-shadow: 0 14px 36px rgba(20, 30, 28, 0.18);
    outline: none;
    animation: filterPanelFade 120ms ease-out;

    @keyframes filterPanelFade {
        from { opacity: 0; }
        to { opacity: 1; }
    }
`;

const PopoverContent = styled.div`
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    overscroll-behavior: contain;
`;

/** 떠 있는 PC 팝업 → 그 팝업을 연 칸. 팝업 안에서 또 팝업을 열었는지(겹친 팝업) 알아보는 데 쓴다 */
const popoverAnchors: WeakMap<Element, HTMLElement | null> = new WeakMap();

/** panel 안의 칸에서 연 팝업(그 팝업 안에서 또 연 것도 포함)인지 */
const isChildPopover = (panel: HTMLElement, popover: Element): boolean => {
    let current: Element | null = popover;
    for (let depth: number = 0; current && depth < 5; depth += 1) {
        const anchor: HTMLElement | null = popoverAnchors.get(current) ?? null;
        if (!anchor) return false;
        if (panel.contains(anchor)) return true;
        current = anchor.closest('[data-filter-popover]');
    }
    return false;
};

/** 누른 곳이 panel 안에서 연 다른 팝업 안인지 */
const isInsideChildPopover = (panel: HTMLElement | null, target: Node): boolean => {
    const popover: Element | null = target instanceof Element ? target.closest('[data-filter-popover]') : target.parentElement?.closest('[data-filter-popover]') ?? null;
    return !!panel && !!popover && popover !== panel && isChildPopover(panel, popover);
};

/** panel 안에서 연 팝업이 지금 떠 있는지 */
const hasOpenChildPopover = (panel: HTMLElement | null): boolean =>
    !!panel && Array.from(document.querySelectorAll('[data-filter-popover]')).some((popover: Element) => popover !== panel && isChildPopover(panel, popover));

/**
 * 칸이 들어 있는 작은 창(MUI Modal 의 창 상자·role=dialog 창) — 팝업 오른쪽 한계로 쓴다. 다른 PC 팝업(필터 창) 안의 칸은 예전처럼 화면 끝만 본다.
 */
const dialogBoxOf = (anchor: HTMLElement): Element | null => {
    const dialog: Element | null = anchor.closest('[role="dialog"]:not([data-filter-popover])');
    if (dialog) return dialog;
    const modal: Element | null = anchor.closest('.MuiModal-root');
    if (!modal || anchor.closest('[data-filter-popover]')) return null;
    return Array.from(modal.children).find((child: Element) => child.contains(anchor)) ?? null;
};

const PopoverPanel = ({ anchorEl, title, subtitle, headerAction, onClose, onApply, onReset, applyLabel, applyDisabled = false, resetLabel = '초기화', popoverWidth = POPOVER_DEFAULT_WIDTH_PX, popoverZIndex, fillBody = false, popoverHeight, renderFooter, children }: FilterPanelProps) => {
    const panelRef = useRef<HTMLDivElement>(null);
    const [position, setPosition] = useState<{ top: number; left: number; width: number; narrow: boolean } | null>(null);
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;

    useRestoreFocus(panelRef);

    // 칩 바로 아래, 화면 오른쪽 밖으로 나가지 않게
    useLayoutEffect(() => {
        const place = (): void => {
            if (!anchorEl) return;
            const rect: DOMRect = anchorEl.getBoundingClientRect();
            const viewWidth: number = document.documentElement.clientWidth;
            // 좌우 한계 — 화면 끝, 칸이 작은 창(MUI 창·확인 창) 안에 있으면 그 창 안쪽(칸과 창 가장자리 사이 여백만큼 들인 곳)도.
            // 2026-10-07 면접 요청 창: 폭 560 달력이 460 창 오른쪽 밖으로 120px 나갔고(fc54e5a1), 오른쪽에 맞추자 이번엔 왼쪽 밖으로 120px 나가며
            // 창 아래 [닫기][면접 요청 보내기]를 덮었다 — 창보다 넓은 팝업은 창 안쪽 폭으로 줄이고 안의 내용이 촘촘한 모양(POPOVER_NARROW_ATTR)으로 바꾼다
            const boxRect: DOMRect | null = dialogBoxOf(anchorEl)?.getBoundingClientRect() ?? null;
            const inset: number = boxRect ? Math.min(DIALOG_INSET_MAX_PX, Math.max(0, Math.min(rect.left - boxRect.left, boxRect.right - rect.right))) : 0;
            const leftLimit: number = Math.max(VIEW_MARGIN_PX, boxRect ? boxRect.left + inset : 0);
            const rightLimit: number = Math.min(viewWidth - VIEW_MARGIN_PX, boxRect ? boxRect.right - inset : Infinity);
            const width: number = Math.min(popoverWidth, rightLimit - leftLimit);
            const narrow: boolean = width < popoverWidth;
            // 칩 왼쪽 끝에 맞추되, 오른쪽이 모자라면(줄 오른쪽 끝의 정렬·창 오른쪽 끝 등) 칩·창 오른쪽 끝에 맞춘다
            const fitsRight: boolean = rect.left + width <= rightLimit;
            const left: number = Math.max(leftLimit, fitsRight ? rect.left : Math.min(rect.right, rightLimit) - width);
            // 높이는 줄인 폭·촘촘한 모양으로 잰다(위로 띄울지 정하기 전에 먼저 걸어 둔다)
            const panel: HTMLDivElement | null = panelRef.current;
            if (panel) {
                panel.style.width = `${width}px`;
                panel.toggleAttribute(POPOVER_NARROW_ATTR, narrow);
            }
            // 아래 자리가 모자라고 위가 더 넓으면 칩 위로 띄운다(MUI 창 안처럼 페이지가 잠겨 내려 볼 수 없을 때 잘리지 않게)
            const viewHeight: number = window.innerHeight;
            const panelHeight: number = panelRef.current?.offsetHeight ?? 0;
            const spaceBelow: number = viewHeight - rect.bottom - POPOVER_GAP_PX - VIEW_MARGIN_PX;
            const spaceAbove: number = rect.top - POPOVER_GAP_PX - VIEW_MARGIN_PX;
            const flipUp: boolean = panelHeight > spaceBelow && spaceAbove > spaceBelow;
            const top: number = flipUp
                ? Math.max(VIEW_MARGIN_PX, rect.top - POPOVER_GAP_PX - panelHeight)
                : rect.bottom + POPOVER_GAP_PX;
            setPosition({ top: top + window.scrollY, left: left + window.scrollX, width, narrow });
        };
        place();
        window.addEventListener('resize', place);
        return () => window.removeEventListener('resize', place);
    }, [anchorEl, popoverWidth]);

    // 이 팝업을 연 칸을 적어 둔다 — 팝업 안에서 또 연 팝업(필터 창 안의 달력)을 "안쪽"으로 알아보는 데 쓴다
    useEffect(() => {
        const panel: HTMLDivElement | null = panelRef.current;
        if (!panel) return undefined;
        popoverAnchors.set(panel, anchorEl ?? null);
        return () => {
            popoverAnchors.delete(panel);
        };
    }, [anchorEl]);

    // 바깥 클릭·Esc 로 닫기(연 칩을 다시 누르는 건 칩이 알아서 닫는다)
    useEffect(() => {
        const onPointerDown = (event: PointerEvent): void => {
            const target = event.target as Node | null;
            if (!target) return;
            if (panelRef.current?.contains(target) || anchorEl?.contains(target)) return;
            // 이 팝업 안에서 연 팝업(창 안의 달력 등)을 누른 것은 바깥이 아니다 — 닫으면 그 팝업도 같이 사라져 아무것도 못 고른다
            if (isInsideChildPopover(panelRef.current, target)) return;
            onCloseRef.current();
        };
        const onKeyDown = (event: KeyboardEvent): void => {
            if (event.key !== 'Escape') return;
            // 이 팝업 안에서 연 팝업이 떠 있으면 Esc 는 그 팝업만 닫는다
            if (hasOpenChildPopover(panelRef.current)) return;
            // 밑에 깔린 MUI 창(쿠폰 창 등)까지 닫히지 않게 먼저 잡아 멈춘다
            event.stopPropagation();
            onCloseRef.current();
        };
        document.addEventListener('pointerdown', onPointerDown);
        window.addEventListener('keydown', onKeyDown, true);
        return () => {
            document.removeEventListener('pointerdown', onPointerDown);
            window.removeEventListener('keydown', onKeyDown, true);
        };
    }, [anchorEl]);

    const close = useCallback((): void => onCloseRef.current(), []);
    const onApplyRef = useRef(onApply);
    onApplyRef.current = onApply;
    const apply = useCallback((): void => (onApplyRef.current ?? onCloseRef.current)(), []);

    const titleId: string = 'filter-popover-title';
    return createPortal(
        <Popover
            ref={panelRef}
            onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) =>
                handleTabEdge(event, panelRef.current, () => {
                    onCloseRef.current();
                    anchorEl?.focus();
                })
            }
            role="dialog"
            aria-labelledby={titleId}
            tabIndex={-1}
            data-filter-popover=""
            {...{ [POPOVER_NARROW_ATTR]: position?.narrow ? '' : undefined }}
            style={{
                ...(position ? { top: position.top, left: position.left, width: position.width } : { visibility: 'hidden', top: 0, left: 0 }),
                ...(popoverZIndex ? { zIndex: popoverZIndex } : {}),
                ...(fillBody && popoverHeight ? { height: popoverHeight } : {}),
            }}
        >
            <TitleRow>
                <Title id={titleId}>{title}</Title>
                {headerAction ?? (subtitle && <Subtitle>{subtitle}</Subtitle>)}
            </TitleRow>
            <PopoverContent style={fillBody ? FILL_BODY_STYLE : undefined}>{renderContent(children, { apply, close })}</PopoverContent>
            {renderFooter ? (
                <Footer compact>{renderFooter({ apply, close })}</Footer>
            ) : applyLabel && (
                <PanelFooter compact applyLabel={applyLabel} applyDisabled={applyDisabled} resetLabel={resetLabel} onReset={onReset} onApply={apply} />
            )}
        </Popover>,
        document.body,
    );
};

const OpenPanel = (props: FilterPanelProps) => {
    const mobile: boolean = useIsMobileWidth();
    return mobile ? <SheetPanel {...props} /> : <PopoverPanel {...props} />;
};

/** 필터 창(폰 시트 / PC 팝업) — 열려 있을 때만 그린다 */
export const FilterPanel = (props: FilterPanelProps) => (props.open ? <OpenPanel {...props} /> : null);
